import { getValueFromInput, parseNumber } from "../utilities";

import $ from "jquery";
import Formatter from "@rmlibrary/formatting";

const PAC_GRAPH_LEGEND_FONT_COLOUR = "#161616"; //#757575
const PAC_GRAPH_LEGEND_FONT_COLOUR_PRINT = "#161616";
const PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR = "#1E3784";
const PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_1_COLOUR = "#829FEC";
const PAC_GRAPH_PAC_SCENARIO_1_COLOUR = "#016DFF";
const PAC_GRAPH_NO_PAC_SCENARIO_2_COLOUR = "#1E3784";
const PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_2_COLOUR = "#829FEC";
const PAC_GRAPH_PAC_SCENARIO_2_COLOUR = "#016DFF";

const lang = document.documentElement.lang.toLowerCase().indexOf("fr") >= 0 ? "fr" : "en";
const formatter = new Formatter(lang);

/**
 * Draw the ticks on the graph's axes.
 * @param {Object} options
 * @param {Object} options.context
 * @param {string} options.text
 * @param {number} options.x
 * @param {number} options.y
 * @param {number} options.index
 * @param {boolean} options.pacXAxis
 * @param {string} options.axis
 */
function drawTicks({ context, text, x, y, index, pacXAxis = false, axis = "x", retirement = false, tickAmount }) {
    const {
        coords: { xAxisBorderHeight, yAxisBorderWidth },
        css: {
            border: { colour, width }
        }
    } = this;
    const start = {
        x: axis === "x" ? x : yAxisBorderWidth,
        y: axis === "x" ? xAxisBorderHeight : y
    };
    const end = {
        x: axis === "x" ? x : yAxisBorderWidth + 5,
        y: axis === "x" ? xAxisBorderHeight - 5 : y
    };

    context.save();
    if (pacXAxis) {
        // Pac graph x-axis
        if (index !== 0 && index !== tickAmount - 1) {
            const fontSize = "14px";
            context.font = `normal ${fontSize} Roobert`;
            context.fillStyle = this.options.isPrint
                ? PAC_GRAPH_LEGEND_FONT_COLOUR_PRINT
                : PAC_GRAPH_LEGEND_FONT_COLOUR;

            const graphWidth = $(context.canvas).outerWidth();
            if (graphWidth > 450) {
                const newText = text.split("\n");

                if (Array.isArray(newText)) {
                    let drawY = y - 20;
                    newText.forEach((line) => {
                        const newLine = line.replace(/\/space\//g, " ");
                        context.fillText(newLine, x, drawY);
                        drawY += 17;
                    });
                } else {
                    context.fillText(text, x, y - 20);
                }
            } else {
                const newText = text.replace(/\n/g, "/space/").split(/\/space\//g);

                if (Array.isArray(newText)) {
                    let drawY = y - 25;
                    newText.forEach((line) => {
                        context.fillText(line, x, drawY);
                        drawY += 17;
                    });
                } else {
                    context.fillText(text, x, y - 20);
                }
            }
        }
    } else if (retirement) {
        const yearString = lang === "en" ? "years" : "ans";

        //Retirement graph x-axis
        if (text === "10 " + yearString || text === "20 " + yearString || text === "30 " + yearString) {
            const fontSize = "14px";

            context.fillStyle = this.options.isPrint
                ? PAC_GRAPH_LEGEND_FONT_COLOUR_PRINT
                : PAC_GRAPH_LEGEND_FONT_COLOUR;
            context.font = `normal ${fontSize} Roobert`;
            context.fillText(text, x, y);
        }
    } else {
        // Y-Axis on both graphs
        if (index !== 0) {
            context.strokeStyle = axis === "x" ? colour.bottom : colour.left;
            context.lineWidth = axis === "x" ? width.bottom : width.left;
            context.beginPath();
            context.moveTo(start.x, start.y);
            context.lineTo(end.x, end.y);
            context.closePath();
            context.stroke();
        }
        context.font = "normal 14px Roobert";
        context.fillStyle = PAC_GRAPH_LEGEND_FONT_COLOUR;
        context.fillText(text, x, y);
    }

    context.restore();
}

/**
 * Prepare savings points for the PAC graph.
 * @param {Object} savings
 * @return {Object[]} savings points
 */
function prepareSavingsPoints(savings, x, graphNum) {
    const scenario1ColourOptions = [
        PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR,
        PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_1_COLOUR,
        PAC_GRAPH_PAC_SCENARIO_1_COLOUR
    ];
    const scenario2ColourOptions = [
        PAC_GRAPH_NO_PAC_SCENARIO_2_COLOUR,
        PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_2_COLOUR,
        PAC_GRAPH_PAC_SCENARIO_2_COLOUR
    ];
    const coloursOptions = graphNum < 1 ? scenario1ColourOptions : scenario2ColourOptions;

    return {
        x: x,
        y: savings.retirement,
        css: {
            colour: coloursOptions[x - 1]
        }
    };
}

/**
 * Prepare the points for the PAC graph.
 * @param {Object} data
 * @param {Object} data.savings.noPAC
 * @param {Object} data.savings.noAnnualIncrease
 * @param {Object} data.savings.pac
 * @return {Object} pac graph points
 */
function preparePACGraphPoints({ savings: { noPAC, noAnnualIncrease, pac } }, graphNum) {
    const pointsArray = [
        prepareSavingsPoints(noPAC, 1, graphNum),
        prepareSavingsPoints(noAnnualIncrease, 2, graphNum),
        prepareSavingsPoints(pac, 3, graphNum)
    ];

    return pointsArray;
}

/**
 * Prepare withdrawals points for the retirement cash flow graph.
 * @param {Object} withdrawals
 * @return {Object[]} withdrawals points
 */
function prepareWithdrawalsPoints({ showBar, withdrawals }) {
    return Object.entries(withdrawals).map(([years, value]) => ({
        x: parseNumber(years),
        y: showBar ? value : 0
    }));
}

/**
 * Prepare the points for the retirement cash flow graph.
 * @param {Object} data
 * @param {Object} data.withdrawals.noPAC
 * @param {Object} data.withdrawals.noAnnualIncrease
 * @param {Object} data.withdrawals.pac
 * @return {Object} retirement cash flow graph points
 */
function prepareRetirementCashFlowGraphPoints({
    barsVisibilityStatus,
    data: {
        withdrawals: { noPAC, noAnnualIncrease, pac }
    }
}) {
    return {
        noPAC: prepareWithdrawalsPoints({
            showBar: barsVisibilityStatus.noPAC,
            withdrawals: noPAC
        }),
        noAnnualIncrease: prepareWithdrawalsPoints({
            showBar: barsVisibilityStatus.noAnnualIncrease,
            withdrawals: noAnnualIncrease
        }),
        pac: prepareWithdrawalsPoints({
            showBar: barsVisibilityStatus.pac,
            withdrawals: pac
        })
    };
}

/**
 * Draw the curves for thr retirement cash flow graph.
 */
function drawCurves() {
    function drawBezierCurve({ x, y }) {
        const context = this.canvas.getContext("2d");
        const cp1 = {
            x: this.coords.yAxisBorderWidth + (x - this.coords.yAxisBorderWidth) / 2,
            y: this.coords.valuesBoundsTop
        };
        const cp2 = {
            x: this.coords.yAxisBorderWidth + (x - this.coords.yAxisBorderWidth) / 2,
            y
        };

        context.save();
        context.strokeStyle = "grey";
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(this.coords.yAxisBorderWidth, this.coords.valuesBoundsTop);
        context.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, x, y);
        context.stroke();
        context.restore();
    }

    drawBezierCurve.call(this, {
        x: this.lines[2].points[0].coords.x2,
        y: this.lines[2].points[0].coords.y2
    });
    drawBezierCurve.call(this, {
        x: this.lines[2].points[1].coords.x2,
        y: this.lines[2].points[1].coords.y2
    });
    drawBezierCurve.call(this, {
        x: this.lines[2].points[2].coords.x2,
        y: this.lines[2].points[2].coords.y2
    });
}

/**
 * Draw the legend for the PAC graph.
 */
function drawLegend({ x, y, graph = "pac" }, legend) {
    const context = this.canvas.getContext("2d");

    let fontSize = 12;
    let legendPadding = window.innerWidth < 576 ? 10 : 20;

    let xStart = window.innerWidth < 576 ? x - 30 : x;

    context.save();

    context.textAlign = "left";
    context.textBaseline = "middle";

    context.fillStyle = graph === "pac" ? PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR : PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR;
    context.beginPath();
    context.arc(xStart, y - 20, 5, 0, 2 * Math.PI);
    context.closePath();
    context.fill();
    context.font = `${fontSize}px Roobert`;
    context.fillStyle = PAC_GRAPH_LEGEND_FONT_COLOUR;
    context.fillText(legend[0], xStart + legendPadding, y - 20);

    context.fillStyle =
        graph === "pac"
            ? PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_1_COLOUR
            : PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_1_COLOUR;
    context.beginPath();
    context.arc(xStart, y, 5, 0, 2 * Math.PI);
    context.closePath();
    context.fill();
    context.font = `${fontSize}px Roobert`;
    context.fillStyle = PAC_GRAPH_LEGEND_FONT_COLOUR;
    context.fillText(legend[1], xStart + legendPadding, y);

    context.fillStyle = graph === "pac" ? PAC_GRAPH_PAC_SCENARIO_1_COLOUR : PAC_GRAPH_PAC_SCENARIO_1_COLOUR;
    context.beginPath();
    context.arc(xStart, y + 20, 5, 0, 2 * Math.PI);
    context.lineWidth = 0;
    context.closePath();
    context.fill();
    context.font = `${fontSize}px Roobert`;
    context.fillStyle = PAC_GRAPH_LEGEND_FONT_COLOUR;
    context.fillText(legend[2], xStart + legendPadding, y + 20);

    context.restore();
}

/**
 * Prepare the axes options for the PAC graph.
 * @param {Object} data
 * @param {Object} data.savings.pac
 * @param {number} data.savings.pac.retirement
 * @return {Object} pac graph axes options
 */
function preparePACGraphAxesOptions(data, barPoints, otherData) {
    const xAxisTicks = barPoints.savings.points;
    const tickAmount = xAxisTicks.length + 2;
    let yAxisTop = otherData
        ? Math.max(data.savings.pac.retirement, otherData.savings.pac.retirement)
        : data.savings.pac.retirement;

    const yearString = lang === "en" ? "year" : "an";

    if (lang == "en") {
        return {
            xAxis: {
                name:
                    data.savings.years > 0
                        ? `In ${data.savings.years} ${yearString}${data.savings.years !== 1 ? "s" : ""}`
                        : "",
                range: [0, tickAmount - 1],
                ticks: tickAmount,
                formatter: (value) => {
                    return data.savings.legend[value - 1];
                },
                drawText: function (context, text, x, y, value, index) {
                    if (value !== 0) {
                        drawTicks.call(this, {
                            context,
                            text,
                            x,
                            y,
                            index,
                            pacXAxis: true,
                            axis: "x",
                            tickAmount
                        });
                    }
                }
            },
            yAxis: {
                range: [0, yAxisTop],
                ticks: 5,
                formatter: () => {
                    return "";
                },
                drawText: function () {
                    // Empty function so nothing draws
                },
                roundBounds: true
            }
        };
    } else {
        return {
            xAxis: {
                name:
                    data.savings.years > 0
                        ? `Dans ${data.savings.years} ${yearString}${data.savings.years !== 1 ? "s" : ""}`
                        : "",
                range: [0, tickAmount - 1],
                ticks: tickAmount,
                formatter: (value) => {
                    return data.savings.legend[value - 1];
                },
                drawText: function (context, text, x, y, value, index) {
                    if (value !== 0) {
                        drawTicks.call(this, {
                            context,
                            text,
                            x,
                            y,
                            index,
                            pacXAxis: true,
                            axis: "x",
                            tickAmount
                        });
                    }
                }
            },
            yAxis: {
                range: [0, yAxisTop],
                ticks: 5,
                formatter: () => {
                    return "";
                },
                drawText: function () {
                    // Empty function so nothing draws
                },
                roundBounds: true
            }
        };
    }
}

/**
 * Prepare the axes options for the retirement cash flow graph.
 * @param {Object} data
 * @param {Object} data.withdrawals.pac
 * @return {Object} retirement cash flow graph axes options
 */
function prepareRetirementCashFlowGraphAxesOptions({ withdrawals: { pac } }) {
    let xAxisTicks = [];
    const xAxisMax = 40;

    for (let i = 0; i <= xAxisMax; i += 5) {
        xAxisTicks.push(i);
    }

    // roundBounds not working, manually rounding yAxis to nearest hundred
    const yRangeMax = Math.ceil((pac["10years"] * 1.1) / 100) * 100;

    const yearString = lang === "en" ? "years" : "ans";

    return {
        xAxis: {
            range: [0, xAxisMax],
            ticks: xAxisTicks.length,
            formatter: (value) => (value === 0 ? "" : `${value} ${yearString}`),
            drawText: function (context, text, x, y, value, index) {
                if (value !== 0) {
                    drawTicks.call(this, {
                        context,
                        text,
                        x,
                        y,
                        index,
                        pacXAxis: false,
                        axis: "x",
                        retirement: true
                    });
                }
            }
        },
        yAxis: {
            range: [0, yRangeMax],
            ticks: 5,
            formatter: (value) => {
                const graphWidth = $(".retirement-graph-container").outerWidth();
                let formattedValue = Number(value).toFixed(0);

                // if (formattedValue >= 1000000) {
                //   formattedValue = formatter.formatCurrency(
                //     formattedValue / 1000000,
                //     2
                //   );
                //   formattedValue = `${formattedValue}m`;
                // } else {
                //   formattedValue = formatter.formatCurrency(formattedValue, 0);
                // }

                formattedValue = formatter.formatCurrency(formattedValue, 0);

                return graphWidth > 450 ? formattedValue : "";
            },
            drawText: function (context, text, x, y, value, index) {
                const graphWidth = $(".retirement-graph-container").outerWidth();
                if (graphWidth > 450) {
                    drawTicks.call(this, {
                        context,
                        text,
                        x,
                        y,
                        index,
                        pacXAxis: false,
                        axis: "y",
                        tickAmount: 5
                    });
                }
            }
        }
    };
}

/**
 * Prepare the bars for the PAC graph.
 * @param {Object} data
 * @return {Object} pac graph bars
 */
function preparePACGraphBars(data, graphNum) {
    const savings = preparePACGraphPoints(data, graphNum);
    const bar = {
        css: {
            line: {
                width: 0
            },
            points: {
                bar: {
                    width: "0.45"
                }
            }
        }
    };
    const colour = graphNum < 1 ? PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR : PAC_GRAPH_NO_PAC_SCENARIO_2_COLOUR;

    return {
        savings: {
            ...bar,
            points: savings,
            css: {
                ...bar.css,
                name: "Savings",
                points: { ...bar.css.points, colour }
            }
        }
    };
}

/**
 * Prepare the bars for the retirement cash flow graph.
 * @param {Object} data
 * @return {Object} retirement cash flow graph bars
 */
function prepareRetirementCashFlowGraphBars({ barsVisibilityStatus, data, scenario }) {
    const { noPAC, noAnnualIncrease, pac } = prepareRetirementCashFlowGraphPoints({
        barsVisibilityStatus,
        data
    });
    const bar = {
        css: {
            line: {
                width: 0
            },
            points: {
                bar: {
                    width: "0.45"
                }
            }
        }
    };
    const contributionAmount = getValueFromInput("#contribution-amount");
    const annualIncreasePercent = getValueFromInput("#annual-increase-percent");
    const annualIncreaseDollar = getValueFromInput("#annual-increase-dollar");
    const annualIncreaseType = $("input[type=radio][name=annualIncreaseType]:checked").val();
    let name = "";

    if (annualIncreaseType === "percent") {
        name = `$${contributionAmount} PAC + ${annualIncreasePercent}%`;
    } else {
        name = `$${contributionAmount} PAC + $${annualIncreaseDollar}`;
    }

    return {
        noPAC: {
            ...bar,
            points: noPAC,
            css: {
                ...bar.css,
                name: "No PAC",
                points: {
                    ...bar.css.points,
                    colour:
                        scenario == "scenario1"
                            ? PAC_GRAPH_NO_PAC_SCENARIO_1_COLOUR
                            : PAC_GRAPH_NO_PAC_SCENARIO_2_COLOUR
                }
            }
        },
        noAnnualIncrease: {
            ...bar,
            points: noAnnualIncrease,
            css: {
                ...bar.css,
                name: `$${contributionAmount} PAC`,
                points: {
                    ...bar.css.points,
                    colour:
                        scenario == "scenario1"
                            ? PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_1_COLOUR
                            : PAC_GRAPH_NO_ANNUAL_INCREASE_SCENARIO_2_COLOUR
                }
            }
        },
        pac: {
            ...bar,
            points: pac,
            css: {
                ...bar.css,
                name: name,
                points: {
                    ...bar.css.points,
                    colour: scenario == "scenario1" ? PAC_GRAPH_PAC_SCENARIO_1_COLOUR : PAC_GRAPH_PAC_SCENARIO_2_COLOUR
                }
            }
        }
    };
}

export {
    drawCurves,
    drawLegend,
    preparePACGraphAxesOptions,
    preparePACGraphBars,
    prepareRetirementCashFlowGraphAxesOptions,
    prepareRetirementCashFlowGraphBars
};
